import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, ExternalLink, Instagram, Award, Inbox } from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://ninjathlete.com';

const dmText = (club, guideSlug) =>
  `Hey ${club.name} 👋 — we just published NinjAthlete's "Best Running Clubs in America (2026)" and you made the cut: ${club.score}/100 (${club.grade}), one of only 32 clubs ranked nationwide.\n\nWe made you a shareable score-card badge — download + suggested caption here:\n${SITE}/run-club-badge/${club.id}\n\nFeel free to post it and tag @ninjathlete. Your full scorecard lives in the guide:\n${SITE}/blogs/article/${guideSlug}#${club.id}`;

export default function RunClubOutreachTab() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchClubs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/runclub-outreach`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(await res.json());
    } catch (err) { console.error('Fetch outreach failed:', err); }
    try {
      const sres = await fetch(`${API_URL}/api/admin/runclub-submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubs(await sres.json());
    } catch (err) { console.error('Fetch submissions failed:', err); }
  }, [token]);

  useEffect(() => { fetchClubs(); }, [fetchClubs]);

  const toggle = async (club) => {
    setData((d) => ({
      ...d,
      clubs: d.clubs.map((c) => (c.id === club.id ? { ...c, contacted: !c.contacted } : c)),
    }));
    try {
      await fetch(`${API_URL}/api/admin/runclub-outreach/${club.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contacted: !club.contacted }),
      });
    } catch (err) { console.error('Toggle contacted failed:', err); fetchClubs(); }
  };

  const copyDm = async (club) => {
    const text = dmText(club, data.guide_slug);
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopiedId(club.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!data) return <LoadingSkeleton />;
  const contacted = data.clubs.filter((c) => c.contacted).length;

  return (
    <div className="space-y-5" data-testid="runclub-outreach-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-4 h-4 text-teal-500" /> Run Club "We Ranked You" Outreach
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Copy the personalized DM, send it via Instagram, then tick the box. Badge pages are public — clubs can download &amp; share without logging in.
          </p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700" data-testid="outreach-progress">
          {contacted} / {data.clubs.length} contacted
        </span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="px-4 py-3">Done</th>
              <th className="px-4 py-3">Club</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3">Instagram</th>
              <th className="px-4 py-3">Badge page</th>
              <th className="px-4 py-3 text-right">DM</th>
            </tr>
          </thead>
          <tbody>
            {data.clubs.map((club) => (
              <tr key={club.id} className={`border-b border-gray-50 last:border-0 ${club.contacted ? 'bg-teal-50/40' : ''}`} data-testid={`outreach-row-${club.id}`}>
                <td className="px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={club.contacted}
                    onChange={() => toggle(club)}
                    className="w-4 h-4 accent-teal-500 cursor-pointer"
                    data-testid={`outreach-check-${club.id}`}
                  />
                </td>
                <td className={`px-4 py-2.5 font-medium ${club.contacted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{club.name}</td>
                <td className="px-4 py-2.5 text-gray-500">{club.city}, {club.state}</td>
                <td className="px-4 py-2.5">
                  <span className="font-semibold text-gray-900">{club.score}</span>
                  <span className="text-gray-400 text-xs"> · {club.grade}</span>
                </td>
                <td className="px-4 py-2.5">
                  <a href={`https://www.instagram.com/${club.ig}/`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline text-xs">
                    <Instagram className="w-3.5 h-3.5" /> @{club.ig}
                  </a>
                </td>
                <td className="px-4 py-2.5">
                  <a href={`/run-club-badge/${club.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gray-500 hover:text-teal-600 text-xs" data-testid={`outreach-badge-link-${club.id}`}>
                    View <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => copyDm(club)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:border-teal-400 hover:text-teal-600 text-gray-600 transition-colors"
                    data-testid={`outreach-copy-dm-${club.id}`}
                  >
                    {copiedId === club.id ? <Check className="w-3.5 h-3.5 text-teal-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedId === club.id ? 'Copied' : 'Copy DM'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Public club submissions from the guide's "Add your run club" form */}
      <div className="space-y-3" data-testid="club-submissions-section">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Inbox className="w-4 h-4 text-teal-500" /> Club Submissions
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500" data-testid="club-submissions-count">
            {subs?.count ?? 0}
          </span>
        </h3>
        {!subs || subs.count === 0 ? (
          <p className="text-xs text-gray-400">
            No submissions yet. The "Add your run club" form on the master guide feeds this queue.
          </p>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                  <th className="px-4 py-3">Club</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Instagram</th>
                  <th className="px-4 py-3">Meets</th>
                  <th className="px-4 py-3">Pace / Sweeper</th>
                  <th className="px-4 py-3">Submitted by</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {subs.submissions.map((s) => (
                  <tr key={s.id} className="border-b border-gray-50 last:border-0 align-top" data-testid={`club-submission-${s.id}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">
                      {s.club}
                      {s.why && <p className="text-[11px] text-gray-400 font-normal mt-0.5 max-w-[220px]">{s.why}</p>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{s.city}</td>
                    <td className="px-4 py-2.5">
                      {s.ig ? (
                        <a href={`https://www.instagram.com/${s.ig.replace(/^@/, '')}/`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline text-xs">{s.ig}</a>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{s.when || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{s.pace || '—'}{s.sweeper ? ` · sweeper: ${s.sweeper}` : ''}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{s.email}</td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">{(s.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
